import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import {
  defaultEmployeeStatusOptions,
  defaultRegistrationStatus,
  defaultTaxBasisIrelandOptions,
  defaultTaxBasisOptions,
} from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { IRELAND } from '@nxsys/store/constants/keys';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { Form, Select } from 'antd';
import { FormProps } from 'antd/lib';
import React, { useMemo } from 'react';

type EmployeesFilterModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onFilter: (params: EmployeesFilterValues) => void;
  payrollCountry?: PayrollCountryType;
};

export type EmployeesFilterValues = {
  status?: string;
  registrationStatus?: string;
  taxBasis?: string[];
  isNotOnboarding?: boolean;
};

const EmployeesFilterModal: React.FC<EmployeesFilterModalProps> = ({
  title,
  isOpen,
  toggle,
  onFilter,
  payrollCountry = 'UNITED_KINGDOM',
}: EmployeesFilterModalProps) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const ableToClearFilter =
    watchValue &&
    (!!watchValue.status ||
      !!watchValue.registrationStatus ||
      watchValue.taxBasis?.length > 0);

  const taxBasisOptions = useMemo(
    () =>
      payrollCountry === IRELAND
        ? defaultTaxBasisIrelandOptions
        : defaultTaxBasisOptions,
    [payrollCountry],
  );

  const statusOptions = useMemo(
    () => [
      { value: 'NOT_ONBOARDING', label: 'Not Send Onboarding yet' },
      ...defaultEmployeeStatusOptions,
    ],
    [],
  );

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    const params: EmployeesFilterValues = {
      status: values?.status || undefined,
      isNotOnboarding:
        values?.status === 'NOT_ONBOARDING' ? true : undefined,
      registrationStatus: values?.registrationStatus || undefined,
      taxBasis: values?.taxBasis?.length ? values.taxBasis : undefined,
    };
    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item
          label={messages['common.employeeStatus']}
          name="status"
          layout="vertical"
        >
          <Select
            allowClear
            placeholder={messages['common.selectEmployeeStatus']}
            options={statusOptions}
            size="large"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          label={messages['table.registrationStatus']}
          name="registrationStatus"
          layout="vertical"
        >
          <Select
            allowClear
            placeholder={messages['common.selectRegistrationStatus']}
            options={defaultRegistrationStatus}
            size="large"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          label={messages['common.taxBasis']}
          name="taxBasis"
          layout="vertical"
        >
          <Select
            mode="multiple"
            allowClear
            placeholder={messages['common.taxBasis']}
            options={taxBasisOptions}
            size="large"
            style={{ width: '100%' }}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default EmployeesFilterModal;
